import { connect } from "react-redux";
import { useState, useEffect } from "react";
import { classNames } from "src/helpers/classNames";
import Input from "src/components/Shared/Forms/Inputs/Input";
import Modal from "src/components/Shared/Modal";
import { apiRequest } from "src/async/apiUtils";
import { toast } from "react-toastify";
import ProfileImage from "../ProfileImage";
import { updateUser } from "src/actions/auth";
import RadioGroup from "src/components/Shared/Forms/Inputs/RadioGroup";
import InformationAlert from "src/components/Shared/Alerts/InformationAlert";
import Button from "src/components/Shared/Buttons/Button";

const UserAddModal = ({ data, isOpen, onCancel = () => {}, onSuccess = () => {}, refreshData = () => {}, user, updateUser }) => {
  const jsonData = {
    name: "",
    email: "",
    image: "",
    password: "",
    send_welcome_email: true,
    confirm_password: "",
  };

  const [formData, setFormData] = useState(jsonData);
  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    setFormData({ ...data, send_welcome_email: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onHandleChange = (key, value) => {
    setFormData((prevData) => ({ ...prevData, [key]: value }));
  };

  const onSubmit = async () => {
    try {
      setIsDisabled(true);
      const url = formData?._id ? "/users/:user_id" : "/users";
      const method = formData?._id ? "PUT" : "POST";
      const { data } = await apiRequest(method, url, { body: formData, params: { user_id: formData?._id } });
      if (data?.data?._id === user?._id) updateUser(data?.data);
      onSuccess();
      refreshData();
      toast.success(data.message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDisabled(false);
    }
  };

  const sendEmail = async () => {
    try {
      setIsDisabled(true);
      const res = await apiRequest("PUT", "/users/:user_id/password", {
        body: {
          send_email: true,
        },
        params: { user_id: data?._id },
      });
      toast.success(res?.data?.message);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDisabled(false);
    }
  };

  return (
    <>
      <Modal
        title="Admin"
        secondaryTitle={data?._id ? "Edit" : "Add"}
        isOpen={isOpen}
        onCancel={onCancel}
        onSuccess={onSubmit}
        isLoading={isDisabled}
        footerleftContent={
          data?._id === user?._id &&
          formData?.send_welcome_email && (
            <Button
              type="button"
              version="default"
              className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
              onClick={sendEmail}
              loading={isDisabled}
              disabled={isDisabled}>
              Send Email
            </Button>
          )
        }>
        <div className="flex w-full flex-wrap gap-y-4">
          <div className="w-full min-w-[200px] px-2">
            <ProfileImage
              user={formData}
              image={formData?.image}
              setImage={(value) => setFormData((prevData) => ({ ...prevData, image: value }))}
            />
          </div>
          <div className="w-full px-2 sm:w-1/2">
            <Input
              autocomplete="off"
              name="full-name"
              label="Full name"
              inline={true}
              type="text"
              value={formData?.name}
              onChange={(e) => setFormData((prevData) => ({ ...prevData, name: e.target.value }))}
            />
          </div>
          <div className="w-full px-2 sm:w-1/2">
            <Input
              name="email"
              label="Email"
              inline={true}
              value={formData?.email}
              onChange={(e) => setFormData((prevData) => ({ ...prevData, email: e.target.value }))}
            />
          </div>
          {(!data?._id || data?._id === user?._id) && (
            <div className="w-full">
              <div className="relative my-3 w-full">
                <RadioGroup
                  title=""
                  subtitle=""
                  options={[
                    { id: "send-email", title: "Send invite email" },
                    { id: "manual", title: "Manually set password" },
                  ]}
                  defaultChecked={formData?.send_welcome_email ? "send-email" : "manual"}
                  onChange={(id) => {
                    onHandleChange("send_welcome_email", id === "send-email");
                  }}
                  containerClasses="ml-1"
                  layout="horizontal"
                />
              </div>
              {formData?.send_welcome_email && (
                <div className="animate-fade-in w-full">
                  <InformationAlert
                    type="default"
                    content={<div>An invite email will be sent to the admin's email address with a link to set up their password.</div>}
                  />
                </div>
              )}
              {!formData?.send_welcome_email && (
                <div className={classNames("grid gap-y-3 transition-all duration-150")}>
                  <div className="animate-fade-in flex w-full gap-x-4 gap-y-2">
                    <div className="w-full">
                      <Input
                        autoComplete="new-password"
                        type="password"
                        name="password"
                        label="Password"
                        value={formData?.password}
                        onChange={(e) => onHandleChange("password", e.target.value.replace(/\s/g, ""))}
                        inline={true}
                      />
                    </div>
                    <div className="w-full">
                      <Input
                        autoComplete="new-password"
                        type="password"
                        name="confirm-password"
                        label="Confirm password"
                        inline={true}
                        value={formData?.confirm_password}
                        onChange={(e) => onHandleChange("confirm_password", e.target.value.replace(/\s/g, ""))}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
    site: state.site,
  };
};

export default connect(mapStateToProps, { updateUser })(UserAddModal);
